import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSite } from '../content/SiteContext';
import { serviceSchema, faqSchema } from '../lib/schema';
import { PageMeta, PageHero, PackageCard, CtaBand, FaqList, Icon, Price, AppButton } from '../components/ui';
import NotFound from './NotFound';

const ServiceDetail = () => {
  const { getServicePage, getPackage, CITIES, ADDONS } = useSite();
  const { slug } = useParams();
  const page = getServicePage(slug);
  if (!page) return <NotFound />;
  const pkg = getPackage(page.packageId);
  const path = `/services/${page.slug}`;
  const extras = page.slug === 'exterior-car-wash' ? [] : ADDONS;

  return (
    <>
      <PageMeta
        title={page.title}
        description={page.description}
        path={path}
        crumbs={[{ name: 'Services', path: '/services' }, { name: page.name, path }]}
        jsonLd={[serviceSchema({ name: page.name, description: page.description, path, offers: [pkg] }), faqSchema(page.faqs || [])]}
      />
      <PageHero eyebrow={`${pkg.name} · AED ${pkg.price}`} title={page.h1}
        actions={<AppButton label={`Book ${pkg.name} in the app`} className="btn btn-primary btn-lg" source={`service_${page.slug}`} params={{ package: pkg.id }} />}>
        <p>{page.intro}</p>
      </PageHero>

      <section className="section">
        <div className="container split split--top">
          <div className="prose">
            {page.sections.map((s) => (
              <React.Fragment key={s.h}>
                <h2>{s.h}</h2>
                <p>{s.p}</p>
              </React.Fragment>
            ))}
            <h2>Available in Abu Dhabi and Dubai</h2>
            <p>
              We carry out the {pkg.name} at homes, residential car parks and offices in{' '}
              {CITIES.map((c, i) => (
                <React.Fragment key={c.id}>
                  {i > 0 && ' and '}<Link to={c.path}>{c.name}</Link>
                </React.Fragment>
              ))}. Send your exact location when you book and we will confirm coverage.
            </p>
            <h2>Price and what to expect</h2>
            <p>
              The {pkg.name} costs AED {pkg.price} and takes {pkg.duration.toLowerCase()} for a typical car.
              For large SUVs, vans or very dirty vehicles we confirm the final price, including any applicable VAT, before the wash starts.
            </p>
          </div>
          <aside className="sticky-aside">
            <PackageCard pkg={pkg} headingLevel={2} showExcludes />
          </aside>
        </div>
      </section>

      {extras.length > 0 && (
        <section className="section section--tint">
          <div className="container">
            <h2 className="h-section">Extras you can add</h2>
            <div className="grid grid-4">
              {extras.map((a) => (
                <div key={a.id} className="addon">
                  <h3>{a.name}</h3>
                  <p>{a.desc}</p>
                  <Price value={a.price} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="section">
        <div className="container container--narrow">
          <h2 className="h-section">{page.name} FAQ</h2>
          <FaqList items={page.faqs} />
          <h2 className="h-section h-section--spaced">Other services</h2>
          <div className="related">
            {page.related.map((r) => {
              const rp = getServicePage(r);
              return (
                <Link key={r} to={`/services/${r}`} className="related__item">
                  <span>{rp.name}</span>
                  <span className="muted">AED {getPackage(rp.packageId).price}</span>
                  <Icon name="arrow" size={16} />
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <CtaBand source={`service_${page.slug}`} />
    </>
  );
};

export default ServiceDetail;
